import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";
import { Input } from "../../../foundation/altzero_base/base/components/ui/input";
import { Badge } from "../../../foundation/altzero_base/base/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../foundation/altzero_base/base/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../foundation/altzero_base/base/components/ui/select";
import { ResponsiveTable } from "../components/ResponsiveTable";
import { useToast } from "../../../foundation/altzero_base/base/hooks/use-toast";
import {
  MapPin,
  Search,
  Trash2,
  ArrowLeft,
  Building2,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import ScopingAILayout from "../components/ScopingAILayout";
import { getSites, deleteSite, SiteDB } from "../services/siteService";
import { usePermissions } from "../../../foundation/altzero_base/base/contextapi/PermissionsContext";
import { canDeleteSites } from "../lib/resourcePermissions";
import { salesforceImportService } from "../services/salesforceImportService";
import { getUserOrganizationId } from "../services/clientService";

type SortField = 'name' | 'nmi' | 'client' | 'current_retailer' | 'current_meter_provider' | 'current_contract_expiry';
type SortDir = 'asc' | 'desc';

const toAbbrev = (name: string | null | undefined): string => {
  if (!name) return '—';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 4).toUpperCase();
  return words.map(w => w[0]).join('').toUpperCase().slice(0, 4);
};

export default function SitesManagement() {
  // The Resource Library had no gating: any role could delete a site. See
  // lib/resourcePermissions.ts.
  const { can } = usePermissions();
  const mayDeleteSites = canDeleteSites(can);
  const { toast } = useToast();
  const [sites, setSites] = useState<SiteDB[]>([]);
  const [filteredSites, setFilteredSites] = useState<SiteDB[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  useEffect(() => {
    loadSites();
  }, []);

  useEffect(() => {
    filterAndSortSites();
  }, [searchQuery, sites, sortField, sortDir]);

  const loadSites = async () => {
    setLoading(true);
    const result = await getSites();
    if (result.success && result.data) {
      setSites(result.data);
      syncMissingSfFields(result.data);
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to load sites",
        variant: "destructive",
      });
    }
    setLoading(false);
  };

  // Background: for sites linked to SF but missing SF-sourced fields, fetch and store them.
  const syncMissingSfFields = async (loadedSites: SiteDB[]) => {
    const needsSync = loadedSites.filter(
      (s) => s.sf_site_id && s.loa_expiry_date === null
    );
    if (needsSync.length === 0) return;

    const orgId = await getUserOrganizationId();
    if (!orgId) return;

    for (const site of needsSync) {
      try {
        await salesforceImportService.syncSiteFieldsFromSf(
          site.id,
          site.client_id,
          orgId,
          site.sf_site_id!
        );
      } catch {
        // non-fatal — site still shows, just without fresh SF data
      }
    }

    // Reload to show updated SF fields
    const refreshed = await getSites();
    if (refreshed.success && refreshed.data) {
      setSites(refreshed.data);
    }
  };

  const filterAndSortSites = () => {
    let result = [...sites];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (site) =>
          site.name?.toLowerCase().includes(query) ||
          site.nmi?.toLowerCase().includes(query) ||
          site.client?.name.toLowerCase().includes(query) ||
          site.current_retailer?.toLowerCase().includes(query) ||
          site.current_meter_provider?.toLowerCase().includes(query)
      );
    }

    result.sort((a, b) => {
      let valA = '';
      let valB = '';
      switch (sortField) {
        case 'name': valA = a.name || ''; valB = b.name || ''; break;
        case 'nmi': valA = a.nmi || ''; valB = b.nmi || ''; break;
        case 'client': valA = a.client?.name || ''; valB = b.client?.name || ''; break;
        case 'current_retailer': valA = a.current_retailer || ''; valB = b.current_retailer || ''; break;
        case 'current_meter_provider': valA = a.current_meter_provider || ''; valB = b.current_meter_provider || ''; break;
        case 'current_contract_expiry': valA = a.current_contract_expiry || ''; valB = b.current_contract_expiry || ''; break;
      }
      const cmp = valA.localeCompare(valB);
      return sortDir === 'asc' ? cmp : -cmp;
    });

    setFilteredSites(result);
  };

  const handleDelete = async (siteId: string) => {
    if (!confirm("Are you sure you want to delete this site?")) return;

    const result = await deleteSite(siteId);
    if (result.success) {
      toast({
        title: "Success",
        description: "Site deleted successfully",
      });
      loadSites();
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to delete site",
        variant: "destructive",
      });
    }
  };

  return (
    <ScopingAILayout>
      <div className="container mx-auto py-6 px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="mb-6 sm:mb-8">
          <Link
            to="/tender/resource-library"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-4 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Resource Library
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 flex items-center gap-3">
                <MapPin className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
                Sites Management
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground mt-2">
                Manage client sites and locations
              </p>
            </div>
          </div>
        </div>

        <Card className="mb-6">
          <CardContent className="pt-6 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
              <Input
                placeholder="Search by name, NMI, client, retailer, or meter provider..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 text-base"
              />
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm text-muted-foreground whitespace-nowrap">Sort by</span>
              <Select value={sortField} onValueChange={(v) => setSortField(v as SortField)}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Site Name</SelectItem>
                  <SelectItem value="nmi">NMI</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="current_retailer">Retailer</SelectItem>
                  <SelectItem value="current_meter_provider">Meter Provider</SelectItem>
                  <SelectItem value="current_contract_expiry">Contract Expiry</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                className="flex items-center gap-1"
              >
                {sortDir === 'asc' ? (
                  <><ArrowUp className="h-3.5 w-3.5" />A–Z</>
                ) : (
                  <><ArrowDown className="h-3.5 w-3.5" />Z–A</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>All Sites ({filteredSites.length})</CardTitle>
            <CardDescription>
              Client sites in your system
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">
                Loading sites...
              </div>
            ) : (
              <ResponsiveTable
                data={filteredSites}
                columns={[
                  {
                    key: "name",
                    label: "Site Name",
                    render: (site) => (
                      <span className="font-medium">{site.name || "—"}</span>
                    ),
                  },
                  {
                    key: "nmi",
                    label: "NMI",
                    render: (site) => site.nmi || "—",
                  },
                  {
                    key: "client",
                    label: "Client",
                    render: (site) => (
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        {site.client?.name || "—"}
                      </div>
                    ),
                  },
                  {
                    key: "address",
                    label: "Address",
                    render: (site) => {
                      const parts = [site.addr_city, site.addr_state].filter(Boolean);
                      return parts.length > 0 ? parts.join(", ") : "—";
                    },
                  },
                  {
                    key: "current_retailer",
                    label: "Retailer",
                    render: (site) => site.current_retailer ? (
                      <Badge variant="outline" className="text-xs font-mono" title={site.current_retailer}>
                        {toAbbrev(site.current_retailer)}
                      </Badge>
                    ) : <span className="text-muted-foreground text-sm">—</span>,
                  },
                  {
                    key: "tariff",
                    label: "Tariff",
                    render: (site) => {
                      const tariff = (site.extn?.network_tariff_code ?? site.extn?.tariff_code) as string | undefined;
                      return tariff
                        ? <span className="text-xs font-mono">{tariff}</span>
                        : <span className="text-muted-foreground text-sm">—</span>;
                    },
                  },
                  {
                    key: "current_meter_provider",
                    label: "Meter",
                    render: (site) => site.current_meter_provider ? (
                      <Badge variant="outline" className="text-xs font-mono" title={site.current_meter_provider}>
                        {toAbbrev(site.current_meter_provider)}
                      </Badge>
                    ) : <span className="text-muted-foreground text-sm">—</span>,
                  },
                  {
                    key: "market_status",
                    label: "Market Status",
                    render: (site) => site.market_status || (
                      <span className="text-muted-foreground text-sm">—</span>
                    ),
                  },
                  {
                    key: "is_embedded",
                    label: "Embedded",
                    render: (site) => site.is_embedded ? (
                      <Badge variant="secondary" className="text-xs">Embedded</Badge>
                    ) : <span className="text-muted-foreground text-sm">—</span>,
                  },
                ]}
                actions={(site) => (
                  <>
                    {mayDeleteSites && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(site.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </>
                )}
                mobileCardRender={(site) => (
                  <div className="space-y-2">
                    <div className="font-medium text-lg">{site.name || "—"}</div>
                    <div className="text-sm text-muted-foreground">
                      NMI: {site.nmi || "—"}
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      {site.client?.name || "—"}
                    </div>
                    {(site.addr_city || site.addr_state) && (
                      <div className="text-sm text-muted-foreground">
                        {[site.addr_city, site.addr_state].filter(Boolean).join(", ")}
                      </div>
                    )}
                    <div className="flex items-center gap-2 flex-wrap pt-1">
                      {site.current_retailer && (
                        <Badge variant="outline" className="text-xs font-mono" title={site.current_retailer}>
                          {toAbbrev(site.current_retailer)}
                        </Badge>
                      )}
                      {site.current_meter_provider && (
                        <Badge variant="outline" className="text-xs font-mono" title={site.current_meter_provider}>
                          {toAbbrev(site.current_meter_provider)}
                        </Badge>
                      )}
                      {site.market_status && (
                        <Badge variant="outline" className="text-xs">{site.market_status}</Badge>
                      )}
                      {site.is_embedded && (
                        <Badge variant="secondary" className="text-xs">Embedded</Badge>
                      )}
                    </div>
                  </div>
                )}
                emptyMessage="No sites found."
              />
            )}
          </CardContent>
        </Card>
      </div>
    </ScopingAILayout>
  );
}

